import { apiClient } from './api-client';

export interface DebtAttachment {
  name: string;
  size: number;
  type: string;
  url: string;
  uploadedAt: string;
}

export interface Debt {
  id: string;
  subscriber_id: string;
  subscriber_name: string;
  subscriber_phone: string;
  amount: number;
  paid_amount: number;
  remaining_amount: number;
  status: 'ACTIVE' | 'PAID' | 'OVERDUE';
  reason: string;
  created_at: string;
  updated_at: string;
  due_date: string;
  notes: string;
  is_installment: boolean;
  installments_count: number;
  last_payment_date: string | null;
  deleted_at: string | null;
  attachments: DebtAttachment[];
}

export interface DebtPayment {
  id: string;
  debt_id: string;
  amount: number;
  payment_date: string;
  payment_method: 'CASH' | 'CARD' | 'TRANSFER' | 'CHEQUE';
  receipt_number: string;
  notes: string;
}

export interface DebtLog {
  id: string;
  debt_id: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'RESTORE' | 'ADD_PAYMENT' | 'UPDATE_PAYMENT' | 'DELETE_PAYMENT';
  description: string;
  old_data: string | null;
  new_data: string | null;
  user_id: string;
  ip_address: string;
  created_at: string;
}

export interface DebtDashboardStats {
  totalDebts: number;
  totalPaid: number;
  totalRemaining: number;
  activeCount: number;
  paidCount: number;
  overdueCount: number;
  topDebtors: Array<{ name: string; total: number; paid: number; remaining: number }>;
  overdueDebtors: Array<{ name: string; daysOverdue: number; amount: number; due_date: string }>;
}

export interface DebtSmartAlerts {
  overdue: Debt[];
  dueToday: Debt[];
  dueIn3Days: Debt[];
  dueIn7Days: Debt[];
}

export class DebtsService {
  private _cachedDebts: Debt[] = [];
  private _cachedPayments: DebtPayment[] = [];
  private _cachedLogs: DebtLog[] = [];
  private _cacheLoaded = false;

  /**
   * Warm client cache by fetching debts, payments, and logs from server
   */
  async warmCache(): Promise<void> {
    try {
      const [debts, payments, logs] = await Promise.all([
        this.getDebts(true),
        this.getPayments(),
        this.getLogs()
      ]);
      this._cachedDebts = debts;
      this._cachedPayments = payments;
      this._cachedLogs = logs;
      this._cacheLoaded = true;
    } catch (err) {
      console.warn('[DebtsService] warmCache failed to refresh server cache:', err);
      // Fail closed: do not fabricate mock debts
      throw err;
    }
  }

  /**
   * Fetch debts from authoritative Worker API
   */
  async getDebts(includeDeleted = false, subscriberId?: string): Promise<Debt[]> {
    const params = new URLSearchParams();
    if (includeDeleted) params.set('include_deleted', 'true');
    if (subscriberId) params.set('subscriber_id', subscriberId);

    const queryStr = params.toString() ? `?${params.toString()}` : '';
    const res = await apiClient.get<{ success: boolean; count: number; data: Debt[] }>(`/debts${queryStr}`);
    const data = res.data?.data || [];
    this._cachedDebts = data;
    return data;
  }

  /**
   * Fetch single debt by ID
   */
  async getDebt(id: string): Promise<Debt | undefined> {
    try {
      const res = await apiClient.get<{ success: boolean; data: Debt }>(`/debts/${encodeURIComponent(id)}`);
      return res.data?.data;
    } catch (err: any) {
      if (err?.status === 404 || err?.statusCode === 404 || err?.response?.status === 404) {
        return undefined;
      }
      throw err;
    }
  }

  /**
   * Fetch payments (optionally filtered by debt_id)
   */
  async getPayments(debtId?: string): Promise<DebtPayment[]> {
    const queryStr = debtId ? `?debt_id=${encodeURIComponent(debtId)}` : '';
    const res = await apiClient.get<{ success: boolean; count: number; data: DebtPayment[] }>(`/debts/payments${queryStr}`);
    const data = res.data?.data || [];
    if (!debtId) {
      this._cachedPayments = data;
    }
    return data;
  }

  /**
   * Fetch debt audit logs (optionally filtered by debt_id)
   */
  async getLogs(debtId?: string): Promise<DebtLog[]> {
    const queryStr = debtId ? `?debt_id=${encodeURIComponent(debtId)}` : '';
    const res = await apiClient.get<{ success: boolean; count: number; data: DebtLog[] }>(`/debts/logs${queryStr}`);
    const data = res.data?.data || [];
    if (!debtId) {
      this._cachedLogs = data;
    }
    return data;
  }

  /**
   * Create a new debt record on server
   */
  async createDebt(debt: Omit<Debt, 'id' | 'paid_amount' | 'remaining_amount' | 'status' | 'created_at' | 'updated_at' | 'deleted_at' | 'installments_count' | 'last_payment_date'>): Promise<Debt> {
    const res = await apiClient.post<{ success: boolean; data: Debt }>('/debts', debt);
    const created = res.data?.data;
    if (created) {
      this._cachedDebts = [created, ...this._cachedDebts.filter(d => d.id !== created.id)];
    }
    return created;
  }

  /**
   * Update existing debt on server
   */
  async updateDebt(id: string, updates: Partial<Pick<Debt, 'amount' | 'due_date' | 'notes' | 'status' | 'reason' | 'attachments'>>): Promise<Debt> {
    const res = await apiClient.put<{ success: boolean; data: Debt }>(`/debts/${encodeURIComponent(id)}`, updates);
    const updated = res.data?.data;
    if (updated) {
      this._cachedDebts = this._cachedDebts.map(d => d.id === id ? updated : d);
    }
    return updated;
  }

  /**
   * Soft-delete debt on server
   */
  async deleteDebt(id: string): Promise<void> {
    await apiClient.delete(`/debts/${encodeURIComponent(id)}`);
    const nowIso = new Date().toISOString();
    this._cachedDebts = this._cachedDebts.map(d => d.id === id ? { ...d, deleted_at: nowIso, updated_at: nowIso } : d);
  }

  /**
   * Restore soft-deleted debt on server
   */
  async restoreDebt(id: string): Promise<Debt> {
    const res = await apiClient.post<{ success: boolean; data: Debt }>(`/debts/${encodeURIComponent(id)}/restore`, {});
    const restored = res.data?.data;
    if (restored) {
      this._cachedDebts = this._cachedDebts.map(d => d.id === id ? restored : d);
    }
    return restored;
  }

  /**
   * Record a payment installment on server
   */
  async createPayment(payment: Omit<DebtPayment, 'id'>): Promise<DebtPayment> {
    const res = await apiClient.post<{ success: boolean; data: DebtPayment }>('/debts/payments', payment);
    const newPayment = res.data?.data;
    if (newPayment) {
      this._cachedPayments = [newPayment, ...this._cachedPayments];
    }
    return newPayment;
  }

  /**
   * Update a payment installment on server
   */
  async updatePayment(id: string, updates: Partial<Omit<DebtPayment, 'id' | 'debt_id'>>): Promise<DebtPayment> {
    const res = await apiClient.put<{ success: boolean; data: DebtPayment }>(`/debts/payments/${encodeURIComponent(id)}`, updates);
    const updated = res.data?.data;
    if (updated) {
      this._cachedPayments = this._cachedPayments.map(p => p.id === id ? updated : p);
    }
    return updated;
  }

  /**
   * Delete a payment installment on server
   */
  async deletePayment(id: string): Promise<void> {
    await apiClient.delete(`/debts/payments/${encodeURIComponent(id)}`);
    this._cachedPayments = this._cachedPayments.filter(p => p.id !== id);
  }

  /**
   * Synchronous dashboard statistics computed from cached debts
   */
  getDashboardStats(): DebtDashboardStats {
    const debts = this._cachedDebts.filter(d => d.deleted_at === null);
    const todayStr = new Date().toISOString().split('T')[0];
    const todayMs = new Date(todayStr).getTime();

    let totalDebts = 0, totalPaid = 0, totalRemaining = 0, activeCount = 0, paidCount = 0, overdueCount = 0;
    const debtorMap: Record<string, { name: string; total: number; paid: number; remaining: number }> = {};
    const overdueDebtors: Array<{ name: string; daysOverdue: number; amount: number; due_date: string }> = [];

    debts.forEach(d => {
      totalDebts += d.amount;
      totalPaid += d.paid_amount;
      totalRemaining += d.remaining_amount;

      let status = d.status;
      if (d.remaining_amount <= 0) {
        status = 'PAID';
      } else if (d.due_date < todayStr) {
        status = 'OVERDUE';
      }

      if (status === 'ACTIVE') activeCount++;
      else if (status === 'PAID') paidCount++;
      else if (status === 'OVERDUE') overdueCount++;

      if (!debtorMap[d.subscriber_name]) {
        debtorMap[d.subscriber_name] = { name: d.subscriber_name, total: 0, paid: 0, remaining: 0 };
      }
      debtorMap[d.subscriber_name].total += d.amount;
      debtorMap[d.subscriber_name].paid += d.paid_amount;
      debtorMap[d.subscriber_name].remaining += d.remaining_amount;

      if (status === 'OVERDUE' && d.remaining_amount > 0) {
        const diffMs = todayMs - new Date(d.due_date).getTime();
        overdueDebtors.push({
          name: d.subscriber_name,
          daysOverdue: Math.max(0, Math.floor(diffMs / 86400000)),
          amount: d.remaining_amount,
          due_date: d.due_date
        });
      }
    });

    const topDebtors = Object.values(debtorMap).sort((a, b) => b.remaining - a.remaining).slice(0, 10);
    overdueDebtors.sort((a, b) => b.daysOverdue - a.daysOverdue);

    return { totalDebts, totalPaid, totalRemaining, activeCount, paidCount, overdueCount, topDebtors, overdueDebtors };
  }

  /**
   * Synchronous smart alerts computed from cached debts
   */
  getSmartAlerts(): DebtSmartAlerts {
    const debts = this._cachedDebts.filter(d => d.deleted_at === null && d.remaining_amount > 0);
    const todayStr = new Date().toISOString().split('T')[0];
    const today = new Date(todayStr);

    const overdue: Debt[] = [];
    const dueToday: Debt[] = [];
    const dueIn3Days: Debt[] = [];
    const dueIn7Days: Debt[] = [];

    debts.forEach(d => {
      const diffDays = Math.ceil((new Date(d.due_date).getTime() - today.getTime()) / 86400000);
      if (d.status === 'OVERDUE' || diffDays < 0) {
        overdue.push(d);
      } else if (diffDays === 0) {
        dueToday.push(d);
      } else if (diffDays <= 3) {
        dueIn3Days.push(d);
      } else if (diffDays <= 7) {
        dueIn7Days.push(d);
      }
    });

    return { overdue, dueToday, dueIn3Days, dueIn7Days };
  }

  /**
   * Synchronous customer debts summary computed from cached debts
   */
  getCustomerDebtsSummary(subscriberId: string) {
    const debts = this._cachedDebts.filter(d => d.subscriber_id === subscriberId && d.deleted_at === null);
    let total = 0, paid = 0, remaining = 0;
    debts.forEach(d => {
      total += d.amount;
      paid += d.paid_amount;
      remaining += d.remaining_amount;
    });
    return { total, paid, remaining, count: debts.length };
  }
}

export const debtsService = new DebtsService();
